/* eslint-disable react-hooks/exhaustive-deps */
import { of } from 'rxjs';
import { emptySelectOption } from '../../../../../components/form/elements/select';
import { useService } from '../../../../../services/services';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '../../../../../hooks/use-query';
import { useObservableState } from 'observable-hooks';
import { useHistory, useParams } from 'react-router-dom';
import { map } from 'rxjs/operators';
import { route } from '../../../../../services/utils';
import { LIVESTOCK_ANIMAL_EVENTS_STARTER_FEED_CREATE } from '../../../../../router/routes';
import StatusEnum from '../enums/event-status';
import { AnimalEventAnimal } from '../../../../../interfaces/animal-event';

export function useStarterFeedState() {
  const { farmId, id } = useParams<{
    farmId: string;
    id: string;
  }>();

  const [params] = useQuery();
  const history = useHistory();
  let allAnimal = useRef(
    ((params['animals'] ? params['animals'] : '').split(',') || []).filter(
      Boolean
    )
  );
  const { animalEventService, animalService } = useService();
  const [animals, setAnimals] = useState<any[]>([]);

  const getTransformProducts = (products) => {
    return products.map((p) => ({
      animal_event_product_id: p.id ? p.id : null,
      product_category_id: p.category,
      product_type_id: p.type,
      product_id: p.product_id,
      product_unit: {
        label: (p.product_unit as string) ?? '',
        value: (p.product_unit as string) ?? '',
      },
      barns: [
        {
          animal_event_product_id: p.id ? p.id : null,
          barn_id: p.warehouse_id,
          product_amount_for_one: {
            amount: p.product_amount_for_one,
            unit: {
              label: (p.product_unit as string) ?? '',
              value: (p.product_unit as string) ?? '',
            },
          },
          product_amount_total: p.product_amount_total,
          product_unit: {
            label: (p.product_unit as string) ?? '',
            value: (p.product_unit as string) ?? '',
          },
          amount_for_one: p.amount_for_one ? p.amount_for_one : '',
          amount_for_one_unit: {
            label: (p.product_unit as string) ?? '',
            value: (p.product_unit as string) ?? '',
          },
        },
      ],
    }));
  };

  const [starterFeedState] = useObservableState<any>(() => {
    if (id) {
      return animalEventService.show(farmId, id).pipe(
        map(({ data }: any) => {
          allAnimal.current = data.animals.map(
            ({ animal_event_animal_id, id }: AnimalEventAnimal) => ({
              animal_event_animal_id,
              id,
            })
          );
          setAnimals(
            data.animals.map((animal) => ({
              ...animal,
              age_days: animal.age_of_days ? animal.age_of_days : null,
            }))
          );

          return {
            species: emptySelectOption,
            barn_ldc_nr: emptySelectOption,
            animal_id: emptySelectOption,
            status: String(data.status),
            date: data.date,
            products: getTransformProducts(data.products),
            notes: data.notes,
            file: data.file ? [data.file] : null,
            animals: data.animals,
          };
        })
      );
    } else {
      return of(defaultState());
    }
  }, null);

  /// get all animals
  useEffect(() => {
    if (!id) {
      const animalIds: string[] | null = params['animals']
        ? ([...new Set(params['animals'].split(','))] as string[])
        : null;

      if (animalIds) {
        setAnimals([]);
        const subscription = animalService
          .indexPost(farmId, { id: animalIds },true)
          .subscribe((response) => {
            setAnimals(
              response.data.map((animal) => ({
                animal_event_animal_id: null,
                animal_id: animal.animal_id,
                breed_code: animal.breed_code,
                id: animal.id,
                name: animal.name,
                age: animal.age,
                age_days: animal.age_of_days ? animal.age_of_days : null,
              }))
            )
          })

        return () => subscription.unsubscribe();
      }
    }
  }, []);

  const removeRow = useCallback(
    (index: number, data: AnimalEventAnimal[]) => {
      const removedAnimal = data[index];

      const updateAnimals = () => {
        allAnimal.current = allAnimal.current.filter(
          (n) => n !== removedAnimal.id
        );
        if (!id) {
          history.push(
            route(
              LIVESTOCK_ANIMAL_EVENTS_STARTER_FEED_CREATE.concat(
                '?animals=:animals'
              ),
              {
                farmId,
                animals: allAnimal.current,
              }
            )
          );
        }
        setAnimals((animals) => animals.filter((_, i) => i !== index));
      };

      updateAnimals();
    },
    [params]
  );

  const addRow = (animal: AnimalEventAnimal) => {
    setAnimals((prevAnimals) => {
      const isAnimalExist = prevAnimals.some(
        (existingAnimal) => existingAnimal.id === animal.id
      );

      if (isAnimalExist) {
        return prevAnimals;
      }

      allAnimal.current.push(animal.id);
      if (!id) {
        history.push(
          route(
            LIVESTOCK_ANIMAL_EVENTS_STARTER_FEED_CREATE.concat(
              '?animals=:animals'
            ),
            {
              farmId,
              animals: allAnimal.current,
            }
          )
        );
      }

      const newAnimal = {
        animal_event_animal_id: null,
        animal_id: animal.animal_id,
        breed_code: animal.breed_code,
        id: animal.id,
        name: animal.name,
        age: animal.age,
        age_days: animal.age_of_days,
      };

      return [newAnimal, ...prevAnimals];
    });
  };

  return [starterFeedState, animals, removeRow, addRow];
}

export function defaultState() {
  return {
    species: emptySelectOption,
    barn_ldc_nr: emptySelectOption,
    animal_id: emptySelectOption,
    status: StatusEnum.planned,
    date: '',
    products: [
      {
        product_category_id: emptySelectOption,
        product_type_id: emptySelectOption,
        product_id: emptySelectOption,
        product_unit: {
          label: '',
          value: '',
        },
        barns: [
          {
            barn_id: emptySelectOption,
            product_amount_for_one: {
              amount: '0',
              unit: emptySelectOption,
            },
            amount_for_one: '0',
            amount_for_one_unit: emptySelectOption,
            product_amount_total: '0',
            product_unit: emptySelectOption,
          },
        ],
      },
    ],
    costs: '',
    file: '',
    animals: [],
  };
}
